<template>
  <div
    v-loading="loading"
    class="grid-view"
    element-loading-text="Loading..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(170,170,170,0.5)"
  >
    <div class="main-content">
      <div class="section operations">
        <div class="triggers">
          <div>
            <el-button type="primary" @click="addBtnMouseClickHandler">{{ $t('CargoMsg.Cargo_Add') }}</el-button>
            <el-button
              v-permission="['button_MTCloseAccount_cancelAccount']"
              :disabled="!selectedNode"
              type="primary"
              @click="delBtnMouseClickHandler"
            >{{ $t('CargoMsg.Cargo_Cancel') }}</el-button>
          </div>
        </div>
      </div>
      <cargo-ag-grid
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :row-data="rowData"
        class="section grid-wrapper"
        @gridReady="params=>{gridApi=params.api}"
      />
    </div>
    <el-dialog v-el-drag-dialog :visible.sync="dateDialog.visible" :title="$t('CargoMsg.Cargo_SelectClosingAccountDate')" :width="$selectByLang('320px', '320px', '320px', '400px', '400px', '400px')" :close-on-click-modal="false">
      <el-form label-width="90px" label-position="left">
        <el-form-item :label="$t('CargoMsg.Cargo_ClosingDate') + ':'">
          <el-date-picker
            v-model="dateDialog.value"
            type="date"
            :clearable="false"
            value-format="yyyy-MM-dd"
            :picker-options="dateDialog.pickerOptions"
            style="width: 100px;"
          />
        </el-form-item>
        <el-radio-group v-model="dateDialog.closingAccountType" style="display: flex;padding-top: 10px;">
          <el-radio :label="0">{{ $t('CargoMsg.Cargo_FinancialClosing') }}</el-radio>
          <el-radio :label="1">{{ $t('CargoMsg.Cargo_BusinessClosing') }}</el-radio>
        </el-radio-group>
      </el-form>
      <span slot="footer">
        <el-button type="primary" @click="dateSelectorHandler">{{ $t('CargoMsg.Cargo_Confirm') }}</el-button>
        <el-button @click="dateDialog.visible=false">{{ $t('Message.Cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import ColumnUtils from '@/utils/ColumnUtils'
import { ClosingAccount } from '@/class/model/act/ClosingAccount'
import { queryCurrentOfficeClosingAccount, checkAccountClosed, saveClosingAccount, deleteClosingAccount } from '@/api/act/closingAccount'

export default {
  name: 'CloseAccount',
  data() {
    const now = new Date()
    let previousMonth = now.setMonth(now.getMonth() - 1)
    previousMonth = ColumnUtils.dateLabelFunc(previousMonth)
    return {
      loading: false,
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true, // 如果为true，则不能选择单元格
        suppressRowClickSelection: true, // 如果为true，则单击行时不会发生行选择
        onRowDataChanged: event => {
          // 清除单选行
          event.api.clearFocusedCell()
        },
        onCellFocused: event => {
          const nodes = event.api.rowModel.rowsToDisplay
          this.selectedNode = nodes[event.rowIndex] || null
        },
        onSortChanged: event => {
          // 重置单选行
          if (this.selectedNode) {
            const rowIndex = this.selectedNode.rowIndex
            event.api.setFocusedCell(rowIndex, event.columnApi.getAllDisplayedColumns()[0])
          }
        },
        context: { componentParent: this }
      },
      columnDefs: [
        {
          headerName: this.$t('CargoMsg.Cargo_AccountKeeper'),
          field: 'inputBy',
          cellRenderer: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ClosingMonth'),
          field: 'closingDate',
          cellRenderer: params => {
            if (params.value) {
              return moment(params.value).format('YYYY-MM')
            }
            return ''
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ClosingBranch'),
          field: 'branchId',
          cellRenderer: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office, 'officeId', params.value, 'chineseName')
          },
          width: 200
        }, {
          headerName: this.$t('CargoMsg.Cargo_OperationDate'),
          field: 'dateCreated',
          cellRenderer: params => {
            return ColumnUtils.dateLabelFunc(params.value)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Auditor'),
          field: 'verifiedBy',
          cellRenderer: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_AccountClosingType'),
          field: 'closingAccountType',
          cellRenderer: params => {
            return ColumnUtils.closingAccountTypeLabel(params.value)
          }
        }
      ],
      rowData: [],
      selectedNode: null,
      gridApi: null,
      dateDialog: {
        visible: false,
        value: '',
        defaultValue: previousMonth,
        pickerOptions: {
          disabledDate: function(curDate) {
            if (curDate) {
              return ColumnUtils.dateLabelFunc(curDate) > previousMonth
            }
            return false
          }
        },
        closingAccountType: 0 // 关账类型
      }
    }
  },
  created() {
    this.queryCloseAccount()
  },
  methods: {
    queryCloseAccount(forbidLoading = false) {
      if (!forbidLoading) {
        this.loading = true
      }
      queryCurrentOfficeClosingAccount({ branchId: this.$store.getters.currentOfficeId }).then(res => {
        this.rowData = res.data
      }).finally(() => {
        if (!forbidLoading) {
          this.loading = false
        }
      })
    },
    addBtnMouseClickHandler() {
      this.dateDialog.value = this.dateDialog.defaultValue
      this.dateDialog.visible = true
    },
    async dateSelectorHandler() {
      try {
        this.loading = true
        const res = await checkAccountClosed({ branchId: this.$store.getters.currentOfficeId, closeAccountDate: this.dateDialog.value })
        if (res.data) {
          this.$message.warning(this.$t('Clearing.TheHasAlreadyBeenClosed'))
          return
        }
        this.dateDialog.visible = false
        const data = new ClosingAccount().fillDefaultValue().getData()
        data.branchId = this.$store.getters.currentOfficeId
        data.inputBy = this.$store.getters.userInfo.userId
        data.closingDate = this.dateDialog.value
        data.closingAccountType = this.dateDialog.closingAccountType
        await saveClosingAccount(data)
        this.$message.success(this.$t('Clearing.ClosingSuccess'))
        await this.queryCloseAccount(true)
      } finally {
        this.loading = false
      }
    },
    delBtnMouseClickHandler() {
      if (!this.selectedNode) {
        this.$message.warning(this.$t('Clearing.PleaseSelectAClosingRecord'))
        return
      }
      const closing = this.selectedNode.data
      if (closing.verifiedBy > 0) {
        this.$message.warning(this.$t('Clearing.TheBeenAuditedAndCancelled'))
        return
      }
      this.$confirm(this.$t('Clearing.AreWantToCancelRecord'), this.$t('Message.Prompt'), {
        confirmButtonText: this.$t('common.btn.ok'),
        cancelButtonText: this.$t('common.btn.cancel'),
        closeOnClickModal: false,
        type: 'warning'
      }).then(() => {
        this.loading = true
        deleteClosingAccount({ uuid: closing.uuid }).then(res => {
          this.$message.success(this.$t('Clearing.ClosingRecordCancelled'))
          this.queryCloseAccount()
        }).catch(() => {
          this.loading = false
        })
      }).catch(() => { })
    }
  }
}
</script>
